/**
 * Show Scheduling Utilities for Admin UI
 *
 * Provides helper functions for resolving show scheduling configuration
 * by combining show-specific overrides with global defaults.
 */

import type { Weekday } from '@musedrops/shared';
import {
  parseWeekdays,
  calculateNextPublishDate,
} from '@musedrops/shared/src/publishScheduling';
import type { AppConfig } from '../services/configApi';

/**
 * Resolved scheduling configuration for a show
 */
export interface ShowSchedule {
  weekdays: Weekday[]; // Array of weekdays (e.g., ['mon', 'wed', 'fri'])
  publishHour: number;
}

/**
 * Show with optional scheduling overrides
 */
export interface ShowWithScheduling {
  customPublishWeekdays?: string | null; // Comma-separated weekdays
}

/**
 * Get the effective scheduling configuration for a show.
 * Combines show-specific overrides with global defaults from config.
 *
 * @param show - Show with optional scheduling overrides
 * @param config - Global app configuration
 * @returns Resolved scheduling configuration
 *
 * @example
 * const schedule = getShowSchedule(show, config);
 * // { weekdays: ['mon', 'wed', 'fri'], publishHour: 13 }
 */
export function getShowSchedule(
  show: ShowWithScheduling,
  config: AppConfig
): ShowSchedule {
  const weekdaysString =
    show.customPublishWeekdays || config.default_publish_weekdays || 'tue';
  const weekdays = parseWeekdays(weekdaysString);

  return {
    weekdays,
    publishHour: config.publish_hour || 13,
  };
}

/**
 * Input shape for a show publish-schedule-violation check (issue #787).
 * Subset of ShowListItem fields needed - kept minimal so callers don't have
 * to build a full Show record just to run the check.
 */
export interface ShowPublishCheckInput extends ShowWithScheduling {
  slug: string;
  enabled: boolean;
  storyCount: number;
}

export interface ShowPublishWarning {
  isViolation: boolean;
  // 'stale' = no published translation found within the lookback window used by
  // getShowPublishStatus (see LAST_PUBLISHED_LOOKBACK_DAYS in data/show.ts) -
  // treated as an unconditional violation regardless of schedule specifics.
  // 'overdue' = schedule-aware check found a due slot in the past.
  reason: 'stale' | 'overdue' | null;
  // Next publish slot the schedule implies, when computable (null for 'stale'
  // or when the show has no valid weekdays configured - see parseWeekdays).
  expectedNextPublishAt: string | null;
}

const NO_VIOLATION: ShowPublishWarning = {
  isViolation: false,
  reason: null,
  expectedNextPublishAt: null,
};

/**
 * Decide whether a show is in violation of its publishing schedule, for the
 * ShowList "Last Published" warning indicator (issue #787).
 *
 * Reuses calculateNextPublishDate (the same algorithm driving the
 * generate-today-stories / scheduled-stories-publish cron flows) rather than
 * re-implementing weekday math - see shared/src/publishScheduling.ts.
 *
 * Trigger conditions (all must hold for isViolation to be true):
 * - show.enabled is true (disabled shows aren't expected to publish)
 * - show.storyCount > 0 (a show with zero stories is still being built, not stalled)
 * - either:
 *   - no published translation was found within the lookback window used by
 *     getShowPublishStatus (lastPublishedAt is null) -> 'stale', unconditional
 *     violation, since the window is already far longer than any real schedule
 *     cadence, or
 *   - calculateNextPublishDate(), given the real lastPublishedAt, returns an
 *     expected next-publish slot that is already <= now -> 'overdue'
 *
 * If the show's effective weekdays are empty (parseWeekdays drops all-invalid
 * config - see PR #793), calculateNextPublishDate throws; that case is treated
 * as "cannot evaluate" (no warning) rather than a false positive.
 */
export function getShowPublishWarning(
  show: ShowPublishCheckInput,
  lastPublishedAt: string | null,
  config: AppConfig,
  now: Date = new Date()
): ShowPublishWarning {
  if (!show.enabled || !show.storyCount) {
    return NO_VIOLATION;
  }

  if (!lastPublishedAt) {
    return { isViolation: true, reason: 'stale', expectedNextPublishAt: null };
  }

  try {
    const todayStr = now.toISOString().slice(0, 10);
    const publishedToday = lastPublishedAt.slice(0, 10) === todayStr;

    const expectedNextPublishAt = calculateNextPublishDate({
      storyId: 'admin-show-list-check',
      showSlug: show.slug,
      customPublishWeekdays: show.customPublishWeekdays ?? null,
      defaultPublishWeekdays: config.default_publish_weekdays || 'tue',
      publishHour: config.publish_hour || 13,
      latestPublishDate: lastPublishedAt,
      // If the last known publish was today, tell the algorithm so it doesn't
      // treat today as still-unpublished (its own BACKFILL branch) and flag a
      // false "overdue" the moment publish hour passes on a normal publish day.
      todayStory: publishedToday
        ? { id: 'last-published', published_at: lastPublishedAt }
        : null,
    });

    const isViolation = new Date(expectedNextPublishAt).getTime() <= now.getTime();
    return {
      isViolation,
      reason: isViolation ? 'overdue' : null,
      expectedNextPublishAt,
    };
  } catch {
    // No valid weekdays configured for this show - can't evaluate the schedule.
    return NO_VIOLATION;
  }
}
